import React, { useCallback, useEffect, useRef, useState } from 'react';
import AnimatedSection from './AnimatedSection';
import SectionIntro from './SectionIntro';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

/** How long each problem holds the stage before the next one takes over. */
const DWELL_MS = 3600;

const problems = [
  {
    title: 'Manual inventory',
    detail:
      'Stock counted on paper at closing, already wrong by the next morning — and nobody finds out until something runs out mid-service.',
  },
  {
    title: 'Revenue leakage',
    detail:
      'Voids, comps, and unbilled items quietly drain margin, and the numbers only surface weeks later when the month is already closed.',
  },
  {
    title: 'Disconnected software',
    detail:
      'A checkout that will not talk to fulfilment, a spreadsheet that will not talk to accounting, and a team retyping the same data three times.',
  },
  {
    title: 'Commission-heavy channels',
    detail:
      'Third-party platforms take a cut of every order and keep the customer relationship that should have been yours.',
  },
  {
    title: 'Paper records',
    detail:
      'Receipts in a drawer and orders on a ticket spike — none of it searchable, none of it a reliable answer when you need one.',
  },
  {
    title: 'Poor reporting',
    detail:
      'Reports that describe last month instead of this shift, so every decision is made looking in the rear-view mirror.',
  },
];

const pillars = ['Orders', 'Preparation', 'Inventory', 'Storefront', 'Fulfilment', 'Customers', 'Staff', 'Branches', 'Accounting', 'Analytics', 'AI'];

const Challenges: React.FC = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const { ref: stageRef, isVisible } = useScrollAnimation(0.25);
  // Bumped on every manual jump so the progress bar restarts its sweep.
  const [cycle, setCycle] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = useCallback((index: number) => {
    setActive(index);
    setCycle((c) => c + 1);
  }, []);

  // Advance only while the section is on screen and the visitor is not reading a
  // specific item, so the rotation never runs unseen in a background tab.
  useEffect(() => {
    if (!isVisible || paused) return undefined;
    timer.current = setTimeout(() => {
      setActive((i) => (i + 1) % problems.length);
    }, DWELL_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [active, isVisible, paused, cycle]);


  return (
    <section className="section-pad reality overflow-hidden">
      <div className="relative max-w-7xl mx-auto">
        <SectionIntro
          align="center"
          label="The Reality"
          title={
            <>
              Running a Business is Complex.
              <br />
              <span className="text-muted">The Software Shouldn&apos;t Be.</span>
            </>
          }
          className="mb-14 md:mb-20"
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Rotating problem */}
          <div
            ref={stageRef}
            className="lg:col-span-7"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={() => setPaused(false)}
          >
            <div className="reality__stage" aria-live="polite">
              {problems.map((p, i) => (
                <div
                  key={p.title}
                  className={`reality__slide ${i === active ? 'is-active' : ''}`}
                  aria-hidden={i !== active}
                >
                  <span className="reality__index font-display text-6xl md:text-8xl font-extrabold block mb-5">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-display text-3xl md:text-5xl font-bold text-white tracking-tight mb-4 text-balance">
                    {p.title}
                  </h3>
                  <p className="text-muted text-base md:text-lg leading-relaxed max-w-xl">{p.detail}</p>
                </div>
              ))}
            </div>

            {/* Progress rail — one tick per problem, click to jump. */}
            <div
              className="mt-10 grid gap-2"
              style={
                {
                  gridTemplateColumns: `repeat(${problems.length}, minmax(0, 1fr))`,
                  '--reality-dwell': `${DWELL_MS}ms`,
                } as React.CSSProperties
              }
            >
              {problems.map((p, i) => (
                <button
                  key={p.title}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Show: ${p.title}`}
                  aria-current={i === active}
                  className={`reality__tick ${i === active ? 'is-active' : ''} ${
                    i < active ? 'is-done' : ''
                  }`}
                />
              ))}
            </div>

            <p className="mt-5 text-xs uppercase tracking-[0.2em] text-muted/60">
              {String(active + 1).padStart(2, '0')} / {String(problems.length).padStart(2, '0')}
              <span className="ml-3 normal-case tracking-normal text-muted/40">
                {paused ? 'Paused' : 'Hover to pause'}
              </span>
            </p>
          </div>

          {/* The answer */}
          <AnimatedSection animation="slide-left" className="lg:col-span-5">
            <div className="relative">
              <span
                className="absolute -left-4 top-1 bottom-1 w-px bg-gradient-to-b from-primary-500 via-primary-500/30 to-transparent"
                aria-hidden
              />
              <p className="text-xs uppercase tracking-[0.2em] text-primary-500 mb-4">One Platform</p>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
                One intelligent system.
              </h3>
              <p className="text-muted leading-relaxed mb-8">
                Replace fragmented tools and manual processes with a unified operating system — POS,
                inventory, storefront, fulfilment, customers, staff, accounting, and analytics working as one.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-3">
                {pillars.map((tag, i) => (
                  <span
                    key={tag}
                    className="relative text-sm font-medium text-white/70 hover:text-white transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-primary-500 after:transition-transform after:duration-500 hover:after:scale-x-100"
                    style={{ transitionDelay: `${i * 30}ms` }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default Challenges;
